import { Box, Button, SxProps, Theme, useTheme } from '@mui/material'

export interface RadioSelectorProps {
  value: string
  values: Record<string, string>
  onClick: (value: string) => void
  disabled?: boolean
  isDisabled?: (value: string) => boolean
  variant: 'primary' | 'secondary' | 'gray'
  sx?: SxProps<Theme>
}

/**
 * Radio button component: one rounded pill holding the options, the selected one shown as a coloured chip.
 */
export function RadioSelector(props: RadioSelectorProps): React.ReactElement {

  const { sx, values, value, onClick, variant, isDisabled, disabled } = props
  const theme = useTheme()
  let bgSelected: string, bgHover: string, textSelected: string

  switch (variant) {
    case 'primary':
    case 'secondary':
      bgSelected = `${variant}.main`
      bgHover = `${variant}.dark`
      textSelected = `${variant}.contrastText`
      break
    case 'gray':
      bgSelected = 'white'
      bgHover = theme.palette.grey[500]
      textSelected = 'black'
      break
  }

  return (
    <Box
      role="radiogroup"
      sx={{
        width: 'fit-content',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        gap: '0.25rem',
        p: '0.25rem',
        borderRadius: '0.5rem',
        backgroundColor: theme.palette.radioSelectorBackground.main,
        ...sx
      }}
    >
      {Object.keys(values).map(name => {
        const selected = name === value
        return (
          <Button
            key={name}
            role="radio"
            aria-checked={selected}
            onClick={() => onClick(name)}
            disabled={disabled || (isDisabled && isDisabled(name))}
            sx={{
              height: '2rem',
              minWidth: 0,
              padding: '0 0.9rem',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: 'normal',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              backgroundColor: selected ? bgSelected : 'transparent',
              color: selected ? textSelected : theme.palette.grey[500],
              '&:hover': {
                backgroundColor: selected ? bgHover : 'rgba(255, 255, 255, 0.08)',
                color: selected ? textSelected : theme.palette.grey[100],
              },
              '&.Mui-disabled': {
                backgroundColor: selected ? undefined : 'transparent !important',
                color: theme.palette.grey[600],
                opacity: 0.6,
              },
            }}
          >
            {values[name]}
          </Button>
        )
      })}
    </Box>
  )
}
